import React from "react";

import { mdiInformationOutline } from "@mdi/js";
import Icon from "@mdi/react";
import { Controller, get, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { APP_TOOLTIP_ID } from "@/src/constants/tooltip";
import { cn } from "@/src/lib/utils";

import ErrorMessage from "./ErrorMessage";
import FieldContainer from "./FieldContainer";
import Label from "./Label";
import { containerClasses, wrapperClasses } from "./styles";
import { Input } from "../ui/input";

import type { JSX } from "react";

// Chromium highlights the currently-edited time segment with the OS system
// colour (blue) instead of the theme accent. Force the focused hour/minute/AM-PM
// fields onto the theme's primary colour so it matches the rest of the app.
const TIME_SEGMENT_CLASSES =
  "[&::-webkit-datetime-edit-hour-field:focus]:bg-primary [&::-webkit-datetime-edit-hour-field:focus]:text-primary-foreground [&::-webkit-datetime-edit-minute-field:focus]:bg-primary [&::-webkit-datetime-edit-minute-field:focus]:text-primary-foreground [&::-webkit-datetime-edit-ampm-field:focus]:bg-primary [&::-webkit-datetime-edit-ampm-field:focus]:text-primary-foreground";

interface TimePickerProps {
  name: string;
  label?: string;
  sublabel?: string;
  tooltip?: string;
  isRequired?: boolean;
  isCompact?: boolean;
  isDisabled?: boolean;
  className?: string;
  min?: string;
  max?: string;
  validate?: (value?: string) => string | boolean;
}

function TimePicker({
  name,
  label,
  sublabel,
  tooltip,
  isRequired,
  isCompact,
  isDisabled,
  className,
  min,
  max,
  validate,
}: TimePickerProps): JSX.Element {
  const {
    control,
    formState: { errors },
    getFieldState,
  } = useFormContext();
  const { t } = useTranslation();

  const error = get(errors, name);

  const isInvalid = getFieldState(name)?.invalid;

  return (
    <FieldContainer className={className} isCompact={isCompact}>
      {label ? (
        <Label
          isCompact={isCompact}
          isRequired={isRequired}
          name={name}
          sublabel={sublabel}
          label={
            tooltip ? (
              <span className="flex items-center gap-[0.3125rem]">
                {label}
                <span
                  className="text-muted-foreground inline-flex"
                  data-tooltip-content={tooltip}
                  data-tooltip-id={APP_TOOLTIP_ID}
                >
                  <Icon path={mdiInformationOutline} size={0.6875} />
                </span>
              </span>
            ) : (
              label
            )
          }
        />
      ) : null}
      <div className={wrapperClasses({ isCompact })}>
        <div className={containerClasses()}>
          <Controller
            control={control}
            name={name}
            render={({ field }): JSX.Element => (
              <Input
                ref={field.ref}
                aria-invalid={isInvalid}
                disabled={isDisabled}
                id={name}
                max={max}
                min={min}
                name={field.name}
                onBlur={field.onBlur}
                step={60}
                type="time"
                value={typeof field.value === "string" ? field.value : ""}
                className={cn(
                  "disabled:bg-muted appearance-none disabled:opacity-100 [&::-webkit-calendar-picker-indicator]:hidden [&::-webkit-calendar-picker-indicator]:appearance-none",
                  TIME_SEGMENT_CLASSES,
                )}
                onChange={(event): void => {
                  field.onChange(event.target.value || undefined);
                }}
              />
            )}
            rules={{
              required:
                isRequired &&
                t("requiredField", {
                  ns: "editor-errors",
                }),
              validate: validate
                ? (value): string | boolean =>
                    validate(typeof value === "string" ? value : undefined)
                : undefined,
            }}
          />
        </div>
        <ErrorMessage error={error} />
      </div>
    </FieldContainer>
  );
}

export default TimePicker;
